import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Modal, Button } from "react-bootstrap";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import { GlobalContext } from "../../../Utils/GlobalContext";

/* ─── Context ──────────────────────────────────────────────────────── */
const FeedbackContext = createContext(null);

/* ─── Toast item ───────────────────────────────────────────────────── */
const TOAST_CFG = {
  success: { bg: "#059669", icon: FaCheckCircle,         label: "Sucesso"   },
  error:   { bg: "#dc3545", icon: FaTimesCircle,         label: "Erro"      },
  warning: { bg: "#f59e0b", icon: FaExclamationTriangle, label: "Atenção"   },
  info:    { bg: "#0d6efd", icon: FaInfoCircle,          label: "Informação" },
};

function ToastItem({ id, type, message, onDismiss }) {
  const cfg = TOAST_CFG[type] || TOAST_CFG.info;
  const Icon = cfg.icon;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        padding: "12px 14px",
        minWidth: 280,
        maxWidth: 380,
        borderLeft: `4px solid ${cfg.bg}`,
        animation: "gpToastIn 0.25s ease",
      }}
    >
      <Icon size={18} color={cfg.bg} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ flex: 1, fontSize: "0.875rem", color: "#212529", lineHeight: 1.45 }}>
        {message}
      </span>
      <button
        onClick={() => onDismiss(id)}
        style={{
          border: "none", background: "none", cursor: "pointer",
          color: "#6c757d", padding: 0, flexShrink: 0, lineHeight: 1,
        }}
        aria-label="Fechar"
      >
        <FaTimes size={13} />
      </button>
    </div>
  );
}

/* ─── Confirm Modal ────────────────────────────────────────────────── */
function ConfirmModal({ show, message, title, type, onConfirm, onCancel, darkMode }) {
  const cfg = TOAST_CFG[type] || TOAST_CFG.warning;
  const Icon = cfg.icon;
  const bg    = darkMode ? "#1c2235" : "#fff";
  const bgH   = darkMode ? "#161b27" : "#f8fafc";
  const borda = darkMode ? "#30364a" : "#dee2e6";
  const texto = darkMode ? "#e2e8f0" : "#212529";
  return (
    <Modal show={show} onHide={onCancel} centered size="sm" backdrop="static">
      <Modal.Header closeButton style={{ background: bgH, borderColor: borda }}>
        <Modal.Title style={{ fontSize: "0.92rem", fontWeight: 600, color: texto, display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={16} color={cfg.bg} />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: bg, color: texto, fontSize: "0.9rem", padding: "20px 20px 16px" }}>
        {message}
      </Modal.Body>
      <Modal.Footer style={{ background: bgH, borderColor: borda, gap: 8 }}>
        <Button variant="outline-secondary" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          size="sm"
          style={{ background: cfg.bg, borderColor: cfg.bg, color: "#fff", fontWeight: 600 }}
          onClick={onConfirm}
        >
          Confirmar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ─── Provider ─────────────────────────────────────────────────────── */
let toastId = 0;

export function FeedbackProvider({ children }) {
  const { darkMode } = useContext(GlobalContext);
  const [toasts, setToasts]       = useState([]);
  const [confirmCfg, setConfirmCfg] = useState(null);
  const resolveRef = useRef(null);

  /* toast ----------------------------------------------------------- */
  const addToast = useCallback((message, type = "info", duration = 4500) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
    }
  }, []);

  // Memoizado: sem isso, toast (e o value do Provider, abaixo) viram uma
  // referência nova a cada render — qualquer efeito/useCallback que dependa
  // de toast (ex.: recarregar dados e mostrar erro) refaz o fetch a cada
  // toast exibido, entrando em loop (fetch falha -> toast -> re-render ->
  // novo toast -> novo fetch -> ...).
  const toast = useMemo(
    () => ({
      success: (msg, d) => addToast(msg, "success", d),
      error: (msg, d) => addToast(msg, "error", d ?? 6000),
      warning: (msg, d) => addToast(msg, "warning", d),
      info: (msg, d) => addToast(msg, "info", d),
    }),
    [addToast]
  );

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /* confirm --------------------------------------------------------- */
  const confirm = useCallback((message, title = "Confirmação", type = "warning") => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmCfg({ message, title, type });
    });
  }, []);

  const handleConfirmAnswer = (answer) => {
    if (resolveRef.current) {
      resolveRef.current(answer);
    }
    resolveRef.current = null;
    setConfirmCfg(null);
  };

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      {/* ── Toast stack (top-right) ─────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        <style>{`
          @keyframes gpToastIn {
            from { opacity: 0; transform: translateX(20px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: "all" }}>
            <ToastItem {...t} onDismiss={dismissToast} />
          </div>
        ))}
      </div>

      {/* ── Confirm modal ──────────────────────────────────────── */}
      {confirmCfg && (
        <ConfirmModal
          show
          message={confirmCfg.message}
          title={confirmCfg.title}
          type={confirmCfg.type}
          darkMode={darkMode}
          onConfirm={() => handleConfirmAnswer(true)}
          onCancel={() => handleConfirmAnswer(false)}
        />
      )}
    </FeedbackContext.Provider>
  );
}

export const useFeedback = () => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error("useFeedback must be used within FeedbackProvider");
  return ctx;
};
